import React, { useState } from 'react';
import { User, Mail, Trash2, Save, AlertTriangle } from 'lucide-react';
import apiClient from '../api/apiClient';

interface AccountSettingsProps {
  user: { email: string; full_name?: string } | null;
  onUserUpdate: (updated: { full_name: string }) => void;
  onAccountDeleted: () => void;
  showNotification: (title: string, message: string, type: 'success' | 'error' | 'info') => void;
}

export const AccountSettings: React.FC<AccountSettingsProps> = ({
  user,
  onUserUpdate,
  onAccountDeleted,
  showNotification,
}) => {
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    if (!fullName.trim()) return;
    setSaving(true);
    try {
      const { data } = await apiClient.patch('/auth/me', { full_name: fullName.trim() });
      onUserUpdate({ full_name: data.full_name });
      showNotification('Profil mis à jour', 'Votre nom a bien été modifié.', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de mettre à jour le profil.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') return;
    setDeleting(true);
    try {
      await apiClient.delete('/auth/me');
      localStorage.removeItem('access_token');
      showNotification('Compte supprimé', 'Votre compte a bien été supprimé.', 'info');
      setTimeout(() => onAccountDeleted(), 1000);
    } catch {
      showNotification('Erreur', 'Impossible de supprimer le compte.', 'error');
      setDeleting(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50 p-8 w-full">

      {/* Titre */}
      <h2 className="text-3xl font-bold text-gray-900 mb-1">Paramètres du compte</h2>
      <p className="text-gray-500 mb-8">Gérez vos informations personnelles.</p>

      {/* Email + Prénom/Nom côte à côte */}
      <div className="grid grid-cols-2 gap-6 mb-6">

        {/* Email */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
              <Mail size={16} className="text-teal-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Adresse e-mail</h3>
          </div>
          <p className="text-sm text-gray-500 mb-1">Compte Google associé</p>
          <p className="text-sm font-medium text-gray-900 bg-gray-50 px-4 py-2.5 rounded-xl border border-gray-200 truncate">
            {user?.email || '—'}
          </p>
          <p className="text-xs text-gray-400 mt-2">L'adresse e-mail ne peut pas être modifiée.</p>
        </div>

        {/* Prénom & Nom */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-cyan-50 flex items-center justify-center">
              <User size={16} className="text-cyan-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Prénom &amp; Nom</h3>
          </div>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="Votre nom complet"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
          />
          <button
            onClick={handleSave}
            disabled={saving || !fullName.trim()}
            className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-semibold rounded-xl shadow hover:from-teal-600 hover:to-cyan-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={15} />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Suppression */}
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
            <Trash2 size={16} className="text-red-500" />
          </div>
          <h3 className="text-base font-semibold text-red-700">Supprimer mon compte</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Cette action est <strong>irréversible</strong>. Toutes vos données (alertes, ventes, achats, stock, bordereaux) seront définitivement supprimées.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-600 border border-red-200 text-sm font-semibold rounded-xl hover:bg-red-100 transition"
        >
          <Trash2 size={15} />
          Supprimer mon compte
        </button>
      </div>

      {/* Modal confirmation suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Supprimer votre compte ?</h3>
                <p className="text-xs text-gray-500 mt-0.5">Cette action est définitive et irréversible.</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              Nous sommes désolés de vous voir partir. Si vous rencontrez un problème, n'hésitez pas à nous contacter à{' '}
              <a href="mailto:contact@smart-resell.com" className="text-teal-600 hover:underline">contact@smart-resell.com</a>{' '}
              — nous ferons notre possible pour vous aider.
            </p>

            <p className="text-sm text-gray-700 mb-2 font-medium">
              Pour confirmer, tapez <span className="font-mono font-bold text-red-600">SUPPRIMER</span> ci-dessous :
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="SUPPRIMER"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-400 transition mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'SUPPRIMER' || deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
